import { Link, Navigate } from "react-router-dom";
import { Show } from "@clerk/react";
import { Star, Inbox, FlaskConical, ChevronLeft, ChevronRight, Loader2, BadgeCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useMe } from "@/lib/me";

export const DemoBadge = () => null;

export const Verified = () => <BadgeCheck className="h-4 w-4 text-primary" aria-label="Verified" />;

export const Stars = ({ avg = 0, count = 0 }) =>
  count ? (
    <span className="inline-flex items-center gap-1 text-sm">
      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
      <span className="font-semibold">{Number(avg).toFixed(1)}</span>
      <span className="text-muted-foreground">({count})</span>
    </span>
  ) : <span className="text-xs text-muted-foreground">No reviews yet</span>;

const STATUS = {
  pending_payment: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  in_progress: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300",
  delivered: "bg-cyan-50 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300",
  revision_requested: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  revoked: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
  inactive: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
  completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  cancelled: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
  refunded: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  disputed: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};

export const StatusBadge = ({ status, testId }) => (
  <span data-testid={testId || "status-badge"} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS[status] || "bg-secondary text-secondary-foreground"}`}>
    {String(status || "").replace(/_/g, " ")}
  </span>
);

export const EmptyState = ({ icon: Icon = Inbox, title, text, action }) => (
  <div data-testid="empty-state" className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-border p-10">
    <div className="rounded-lg bg-accent p-3 text-accent-foreground"><Icon className="h-5 w-5" /></div>
    <h3 className="text-lg font-semibold">{title}</h3>
    {text && <p className="max-w-md text-sm text-muted-foreground">{text}</p>}
    {action}
  </div>
);

export const PageHeader = ({ eyebrow, title, text, children }) => (
  <div className="flex flex-col gap-4 border-b border-border pb-8 md:flex-row md:items-end md:justify-between">
    <div className="max-w-2xl">
      {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
      <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{title}</h1>
      {text && <p className="mt-3 text-base text-muted-foreground">{text}</p>}
    </div>
    {children}
  </div>
);

export const GridSkeleton = ({ n = 6 }) => (
  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className="card-surface p-4"><Skeleton className="mb-4 h-40 w-full" /><Skeleton className="mb-2 h-4 w-3/4" /><Skeleton className="h-4 w-1/2" /></div>
    ))}
  </div>
);

export const Spinner = () => <Loader2 className="h-5 w-5 animate-spin text-primary" />;

export const Pager = ({ page, pages, onChange }) =>
  pages > 1 ? (
    <div className="mt-10 flex items-center gap-3" data-testid="pagination">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" /></Button>
      <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="pagination-next"><ChevronRight className="h-4 w-4" /></Button>
    </div>
  ) : null;

export const Tag = ({ children }) => <Badge variant="secondary" className="rounded-md font-medium">{children}</Badge>;

export function RequireAuth({ children, admin = false, vendor = false }) {
  const { me, loading, error } = useMe();
  return (
    <>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
      <Show when="signed-in">
        {loading && !me ? <div className="container-x py-24"><Spinner /></div>
          : error && !me ? <div className="container-x py-24"><EmptyState title="Account unavailable" text={error} /></div>
          : admin && me?.role !== "admin" ? <div className="container-x py-24"><EmptyState title="Admins only" text="You don't have access to this area." /></div>
          : vendor && !me?.vendor && !(me?.team_of || []).length ? (
            <div className="container-x py-24"><EmptyState title="Become a seller first" text="Create your developer or agency storefront to sell services and products."
              action={<Button asChild data-testid="become-seller-cta"><Link to="/dashboard/vendor/storefront">Create storefront</Link></Button>} /></div>
          ) : children}
      </Show>
    </>
  );
}
